import React, {useRef, useState} from "react"
import {useTranslation} from "react-i18next"
import {createRoot} from "react-dom/client"
import {useLocation, useNavigate} from "react-router-dom"

import {useRecoilState, useSetRecoilState} from "recoil"

import {CButton, CDropdownItem, CModal, CModalBody, CModalFooter, CModalHeader, CToast, CToastBody, CToastClose, CToaster, CTooltip} from "@coreui/react-pro"
import CIcon from "@coreui/icons-react"
import {cilTrash} from "@coreui/icons"

import {authAtom} from "@asset/AuthWrapper"
import ErrorWrapper from "@asset/ErrorWrapper"
import {AbortWrapper} from "@asset/AbortWrapper"
import {GlobalState} from "@asset/Provider"
import {ErrorHandle} from "@asset/exceptions/ErrorHandle"
import {ErrorAuthHandle} from "@asset/exceptions/ErrorAuthHandle"

import _paths from "@src/_api"

export function FetchWrapper() {
    const {t} = useTranslation()

    const [auth, setAuth] = useRecoilState(authAtom)
    const [isLoading, setIsLoading] = useState(true)

    const dialog = DialogWrapper()
    const {createAbort} = AbortWrapper()

    const authHeader = (url, header) => {
        const token = auth?.token
        const isLoggedIn = !!token
        const isLoginUrl = url.startsWith(_paths.auth + "login")

        if (header)
            header = {
                ...{
                    "Accept": 'application/json',
                    "Content-Type": 'application/json',
                    "Access-Control-Allow-Origin": '*'
                }, ...header
            }

        if (isLoggedIn && !isLoginUrl)
            header = {
                ...header, ...{
                    Authorization: `Bearer ${token}`,
                }
            }

        return header
    }

    return {
        loading: () => {
            return isLoading
        },
        forceLoading: (loading) => {
            setIsLoading(loading)
        },
        get: request("GET"),
        post: request("POST"),
        put: request("PUT"), //UPDATE
        patch: request("PATCH"), //UPDATE
        delete: request("DELETE"),
        download: request("POST", handleDownload),
        upload: request("POST", handleResponse, false),
    };

    function request(method, handle = handleResponse, header = {}) {
        return async (url, credentials = null) => {
            let baseUrl;
            let abortController = createAbort()

            //Remove all keys from credentials.filter with value "ignore"
            if (credentials?.filter)
                Object.keys(credentials.filter).forEach(key => {
                    if (credentials.filter[key] === "ignore")
                        delete credentials.filter[key]
                })

            if (!url)
                if (dialog.danger("Url is empty"))
                    return Promise.reject("Url is empty")

            if (url.includes("http") || url.includes("https")) baseUrl = url
            else baseUrl = _paths.base + url

            const requestOptions = {
                method: method,
                signal: abortController.signal,
                headers: authHeader(baseUrl, header)
            }

            if (credentials)
                requestOptions.body = (header) ? JSON.stringify(credentials) : credentials

            try {
                return await fetch(baseUrl, requestOptions).then(handle)
            } catch (error) {
                if (error.name === "AbortError")
                    return Promise.reject("Request aborted")
            }
        }
    }

    async function handleResponse(response) {
        try {
            return await response.text().then(data => {
                //Loading
                setIsLoading(false)

                //STATUSES
                if ([401, 403].includes(response.status) && auth?.token) //429
                    setAuth(null)
                if ([413].includes(response.status))
                    dialog.danger(t("error.fileSize"))
                if ([404].includes(response.status))
                    dialog.danger(t("error.notFound"))
                if ([406].includes(response.status))
                    throw new ErrorAuthHandle()

                const text = data && JSON?.parse(data)

                if (!response.ok)
                    throw new ErrorHandle({text: text, response: response})

                if (text.token)
                    return text

                if (text.message === true)
                    return text.response
                // else
                //     throw new ErrorHandle({text: text, response: response})
            })
        } catch (error) {
            if (error.name === "ErrorAuthHandle")
                return !dialog.danger(t("error.notAuthorized"))

            const errorPromise = (error.text && error.text.message) || response.statusText
            const alertElement = document.getElementById('alert')
            const alert = createRoot(alertElement)

            alert.render(
                <ErrorWrapper message={errorPromise} error={error.text}/>
            )

            return Promise.reject(errorPromise)
        }
    }

    function handleDownload(response) {
        try {
            if (response.headers.get("Content-Disposition") === null)
                return handleResponse(response)

            return response.blob().then(file => {
                if ([401, 403].includes(response.status) && auth?.token) //429
                    setAuth(null)

                let link = document.createElement("a")
                link.href = window.URL.createObjectURL(file)
                link.download = response.headers.get("Content-Disposition").split("filename=")[1]
                link.download = link.download.replaceAll("\"", "")
                link.click()

                return true
            })
        } catch (error) {
            const errorPromise = (error.text && error.text.message) || response.statusText

            const alertElement = document.getElementById('alert')
            const alert = createRoot(alertElement)

            alert.render(
                <ErrorWrapper message={errorPromise} error={error.text}/>
            )

            return Promise.reject(errorPromise)
        }
    }

}

export const DeleteWrapper = ({id, path, callback, button = true, reload = false, disabled = false}) => {
    const {t} = useTranslation()

    const [visible, setVisible] = useState(false)

    const dialog = DialogWrapper()
    const fetchWrapper = FetchWrapper()
    const [state, dispatch] = GlobalState()

    const submit = (check) => {
        setVisible(!visible)
        if (check)
            fetchWrapper.delete(path + id).then((data) => {
                if (dialog.success(data))
                    // if prop.state is function
                    if (typeof (callback) === "function")
                        callback()
                    else if (typeof (dispatch) === "function")
                        dispatch({refresh: !state.refresh})
                    else if (reload)
                        window.location.href = reload
            })
    }

    return (
        <>
            <CTooltip content={t("const.delete")}>
                {!button ?
                    <CDropdownItem onClick={() => setVisible(true)} disabled={disabled}><CIcon icon={cilTrash}/> {t("modal.delete")}</CDropdownItem> :
                    <CButton type={"button"} color="danger" className={"m-1"} size={"sm"} onClick={() => setVisible(true)} disabled={disabled}>
                        <CIcon icon={cilTrash}/>
                    </CButton>
                }
            </CTooltip>
            <CModal visible={visible} onClose={() => setVisible(false)}>
                <CModalHeader closeButton>{t("const.delete.title")}</CModalHeader>
                <CModalBody>
                    {t("const.delete.confirm")}
                </CModalBody>
                <CModalFooter>
                    <CButton color={"success"} onClick={() => submit(true)}>{t("const.yes")}</CButton>
                    <CButton color={"danger"} onClick={() => submit(false)}>{t("const.no")}</CButton>
                </CModalFooter>
            </CModal>
        </>
    )
}

export function LoginWrapper() {
    const navigate = useNavigate()
    const state = useLocation()
    const setAuth = useSetRecoilState(authAtom)

    const fetchWrapper = FetchWrapper()
    const dialog = DialogWrapper()

    return {
        login,
        logout
    }

    async function login(email, password) {
        const baseUrl = _paths.auth + "login"
        const admin = await fetchWrapper.post(baseUrl, {email, password})

        localStorage.setItem("admin", JSON.stringify(admin))
        localStorage.setItem("avatar", ((admin.avatar?.includes("base64,")) ? "" : "data:image/jpeg;charset=utf-8;base64,") + admin.avatar)

        setAuth(admin)
    }

    async function logout() {
        const baseUrl = _paths.base + "logout"
        await fetchWrapper.post(baseUrl).then((data) => {
            if (dialog.success(data)) {
                sessionStorage.clear()
                localStorage.clear()
                localStorage.setItem("previewPath", state.pathname)

                // echo.channel("api-rest-channel").unsubscribe()

                setAuth(null)
                //Go to page login
                navigate("/")
            }
        })
    }
}

export function DialogWrapper() {
    const {t} = useTranslation()
    const toaster = useRef()

    return {
        success: dialog("success"),
        primary: dialog("primary"),
        info: dialog("info"),
        danger: dialog("danger")
    }

    function dialog(type) {
        return (message) => {
            const dialogToast = (
                <CToast title={t("success.title")} color={type} className="text-white align-items-center">
                    <div className="d-flex">
                        <CToastBody>{message}</CToastBody>
                        <CToastClose className="me-2 m-auto"/>
                    </div>
                </CToast>
            )

            const alertElement = document.getElementById('alert')
            const alert = createRoot(alertElement)

            alert.render(
                <CToaster ref={toaster} push={dialogToast} placement="top-end" className={"mt-3 me-3"}/>
            )

            return true;
        }
    }
}

export function DataWrapper() {
    const fetchWrapper = FetchWrapper()

    return {
        getAdmins,
        getTranslates,
        getLanguage,
        getAvatars,
    }

    async function getAdmins({provider = null} = {}) {
        const result = await fetchWrapper.post(_paths.management.admins + "search", {
            limit: 1000,
            page: 1,
            filter: {provider: provider}
        })
        let items = [...result.items]

        if (provider === null)
            localStorage.setItem("admins", JSON.stringify(
                items.map((item) => {
                    return {
                        value: item.id,
                        label: item.fname + ' ' + item.lname
                    }
                })
            ))
        else return items
    }

    async function getTranslates(lang) {
        const result = await fetchWrapper.get(_paths.system.translations + lang)

        localStorage.setItem(lang, result)
    }

    async function getLanguage(lang) {
        return await fetchWrapper.get(_paths.system.translations + lang)
    }

    async function getAvatars() {
        return await fetchWrapper.get(_paths.management.admins + "avatars").then((result) => {
            localStorage.setItem("avatars", true)

            result.map((item) => {
                localStorage.setItem("avatar" + item.adminId, ((item.file?.includes("base64,")) ? "" : "data:image/jpeg;charset=utf-8;base64,") + item.file)
            })
        })
    }
}

export function Refresher() {
    const location = useLocation()
    const navigate = useNavigate()

    return {
        to: refreshTo,
    };

    function refreshTo(goTo = null) {
        navigate("/refresh")
        setTimeout(() => {
            navigate(goTo ?? location.pathname)
        }, 200)
    }
}
